import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";
import { getLibraries } from "@/api/endpoints";
import { NowPlayingBar } from "@/components/NowPlayingBar";
import { PluginNavItems } from "@/components/PluginNavItems";
import { AdminPluginNavItems } from "@/components/AdminPluginNavItems";
import { TopbarSearch } from "@/components/TopbarSearch";
import { clientBuildDisplayLabel, clientBuildTitle } from "@/lib/buildInfo";
import { pluginEmbedPath, pluginLibraryUIURL } from "@/lib/pluginLibrary";
import { useClientBuildFreshness } from "@/hooks/useClientBuildFreshness";
import clsx from "clsx";

const LIBRARY_ICONS: Record<string, string> = {
  movies: "film",
  tv: "tv",
  music: "music",
  photos: "image",
};

export function AppShell() {
  const { user, logout } = useAuth();
  const libraries = useApi(() => getLibraries(), []);
  const buildFreshness = useClientBuildFreshness();
  const refetchLibraries = libraries.refetch;
  const navigate = useNavigate();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchFocusSignal, setSearchFocusSignal] = useState(0);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((s) => !s), []);
  const toggleMobileDrawer = useCallback(() => setMobileDrawerOpen((s) => !s), []);
  const showSidebarLabels = !sidebarCollapsed || mobileDrawerOpen;

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "/") {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        setSearchFocusSignal((value) => value + 1);
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    function handleLibrariesChanged() {
      refetchLibraries();
    }
    window.addEventListener("knotfin:libraries-changed", handleLibrariesChanged);
    return () => window.removeEventListener("knotfin:libraries-changed", handleLibrariesChanged);
  }, [refetchLibraries]);

  function handleNavClick() {
    setMobileDrawerOpen(false);
  }

  return (
    <div className="app-shell">
      {/* Mobile backdrop */}
      {mobileDrawerOpen && (
        <div className="drawer-backdrop" onClick={() => setMobileDrawerOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={clsx("sidebar", {
          "sidebar--collapsed": sidebarCollapsed,
          "sidebar--mobile-open": mobileDrawerOpen,
        })}
      >
        <div className="sidebar-header">
          <button className="sidebar-toggle" onClick={toggleSidebar} title="Toggle sidebar">
            <Icon name="menu" />
          </button>
          <span className="logo-mark-frame" aria-hidden="true">
            <img className="logo-mark" src="/knotfin-logo-v3.png" alt="" />
          </span>
          {showSidebarLabels && <span className="logo">KnotFin</span>}
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/" className="nav-item" onClick={handleNavClick}>
            <Icon name="home" />
            {showSidebarLabels && <span>Home</span>}
          </NavLink>
          {user?.attributes.can_request && (
            <NavLink to="/requests" className="nav-item" onClick={handleNavClick}>
              <Icon name="inbox" />
              {showSidebarLabels && <span>Requests</span>}
            </NavLink>
          )}
          <PluginNavItems showLabels={showSidebarLabels} onNavigate={handleNavClick} />

          <div className="nav-section-label">
            {showSidebarLabels && "Libraries"}
          </div>
          <NavLink to="/collections" className="nav-item" onClick={handleNavClick}>
            <Icon name="collection" />
            {showSidebarLabels && <span>Collections</span>}
          </NavLink>
          {libraries.data?.data.map((lib) => {
            const icon = (
              <Icon name={LIBRARY_ICONS[lib.attributes.library_type] ?? "folder"} />
            );
            const label = showSidebarLabels && <span>{lib.attributes.name}</span>;
            // Plugin-provided pseudo-libraries open the plugin's own UI (served
            // over the reverse tunnel) inside the shell, not the filesystem
            // library page.
            const pluginUrl = pluginLibraryUIURL(lib);
            if (pluginUrl) {
              return (
                <NavLink
                  key={lib.id}
                  to={pluginEmbedPath(pluginUrl)}
                  className="nav-item"
                  onClick={handleNavClick}
                >
                  {icon}
                  {label}
                </NavLink>
              );
            }
            return (
              <NavLink
                key={lib.id}
                to={`/libraries/${lib.id}`}
                className="nav-item"
                onClick={handleNavClick}
              >
                {icon}
                {label}
              </NavLink>
            );
          })}

          {user?.attributes.is_admin && (
            <>
              <div className="nav-section-label">
                {showSidebarLabels && "Admin"}
              </div>
              <NavLink to="/admin/users" className="nav-item" onClick={handleNavClick}>
                <Icon name="users" />
                {showSidebarLabels && <span>Users</span>}
              </NavLink>
              <NavLink to="/admin/plugins" className="nav-item" onClick={handleNavClick}>
                <Icon name="puzzle" />
                {showSidebarLabels && <span>Plugins</span>}
              </NavLink>
              <NavLink to="/admin/jobs" className="nav-item" onClick={handleNavClick}>
                <Icon name="activity" />
                {showSidebarLabels && <span>Jobs</span>}
              </NavLink>
              <NavLink to="/admin/activity" className="nav-item" onClick={handleNavClick}>
                <Icon name="activity" />
                {showSidebarLabels && <span>Activity</span>}
              </NavLink>
              <NavLink to="/admin/metadata" className="nav-item" onClick={handleNavClick}>
                <Icon name="database" />
                {showSidebarLabels && <span>Metadata</span>}
              </NavLink>
              <AdminPluginNavItems showLabels={showSidebarLabels} onNavigate={handleNavClick} />
            </>
          )}

          <div className="nav-section-label">
            {showSidebarLabels && "Settings"}
          </div>
          <NavLink to="/settings" className="nav-item" onClick={handleNavClick}>
            <Icon name="user" />
            {showSidebarLabels && <span>Account</span>}
          </NavLink>
          {user?.attributes.is_admin && (
            <NavLink to="/admin/settings" className="nav-item" onClick={handleNavClick}>
              <Icon name="settings" />
              {showSidebarLabels && <span>Server</span>}
            </NavLink>
          )}
          <NavLink to="/api-docs" className="nav-item" onClick={handleNavClick}>
            <Icon name="file-text" />
            {showSidebarLabels && <span>API Docs</span>}
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          {showSidebarLabels && (
            <>
              <span className="username">{user?.attributes.display_name}</span>
              <span
                className={clsx("client-build-label", buildFreshness.stale && "client-build-label--stale")}
                title={clientBuildTitle()}
              >
                {clientBuildDisplayLabel()}
              </span>
            </>
          )}
          <button onClick={logout} className="nav-item nav-item--subtle" title="Sign out">
            <Icon name="log-out" />
            {showSidebarLabels && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="main-area">
        {/* Top bar */}
        <header className="topbar">
          <button className="topbar-hamburger" onClick={toggleMobileDrawer}>
            <Icon name="menu" />
          </button>

          <TopbarSearch focusSignal={searchFocusSignal} icon={<Icon name="search" />} />

          <div className="topbar-actions">
            <button className="topbar-btn" title="Account settings" onClick={() => navigate("/settings")}>
              <Icon name="user" />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="content">
          <Outlet />
        </main>

        {/* Now playing bar */}
        <NowPlayingBar />
      </div>

    </div>
  );
}

function Icon({ name }: { name: string }) {
  return (
    <span className="icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        {renderIconPath(name)}
      </svg>
    </span>
  );
}

export { Icon };

function renderIconPath(name: string) {
  switch (name) {
    case "menu":
      return (
        <>
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </>
      );
    case "home":
      return (
        <>
          <path d="M4 11.5L12 5l8 6.5" />
          <path d="M6.5 10.5V19h11v-8.5" />
        </>
      );
    case "film":
      return (
        <>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <path d="M8 5v14" />
          <path d="M16 5v14" />
          <path d="M4 9h4" />
          <path d="M16 9h4" />
          <path d="M4 15h4" />
          <path d="M16 15h4" />
        </>
      );
    case "collection":
      return (
        <>
          <rect x="4" y="5" width="13" height="15" rx="2" />
          <path d="M8 5v15" />
          <path d="M17 8h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2h-1" />
          <path d="M4 10h4" />
          <path d="M4 15h4" />
          <path d="M13 10h4" />
          <path d="M13 15h4" />
        </>
      );
    case "tv":
      return (
        <>
          <rect x="4" y="6" width="16" height="11" rx="2" />
          <path d="M9 20h6" />
          <path d="M12 17v3" />
        </>
      );
    case "music":
      return (
        <>
          <path d="M15 6v9.5a2.5 2.5 0 1 1-2-2.45V8.2l7-1.7v7a2.5 2.5 0 1 1-2-2.45V4.8z" />
        </>
      );
    case "image":
      return (
        <>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="M20 15l-4-4-6 6" />
        </>
      );
    case "folder":
      return (
        <>
          <path d="M4 8a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
        </>
      );
    case "search":
      return (
        <>
          <circle cx="11" cy="11" r="6" />
          <path d="M20 20l-4.2-4.2" />
        </>
      );
    case "inbox":
      return (
        <>
          <path d="M5 5h14l1.5 10.5A3 3 0 0 1 17.5 19h-11a3 3 0 0 1-3-3.5z" />
          <path d="M8 13h1.8a2.2 2.2 0 0 0 4.4 0H16" />
        </>
      );
    case "user":
      return (
        <>
          <circle cx="12" cy="8.5" r="3.25" />
          <path d="M6 19a6 6 0 0 1 12 0" />
        </>
      );
    case "users":
      return (
        <>
          <circle cx="9" cy="9" r="2.75" />
          <path d="M4.5 18a4.75 4.75 0 0 1 9 0" />
          <circle cx="17" cy="9.5" r="2.25" />
          <path d="M15 18a4.2 4.2 0 0 1 4.5-3" />
        </>
      );
    case "puzzle":
      return (
        <>
          <path d="M9 4h3a2 2 0 0 1 2 2v1.1a1.9 1.9 0 1 0 0 3.8V12h1a2 2 0 0 1 2 2v6H7a2 2 0 0 1-2-2v-3h1.1a1.9 1.9 0 1 0 3.8 0H11V14a2 2 0 0 1 2-2h1v-1.1a1.9 1.9 0 1 0-3.8 0V12H9a2 2 0 0 1-2-2V4z" />
        </>
      );
    case "activity":
      return (
        <>
          <path d="M4 14h3l2.2-5 3.4 9 2.2-5H20" />
        </>
      );
    case "settings":
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1 1 0 0 0 .2 1.1l.1.1a1 1 0 0 1 0 1.4l-1.3 1.3a1 1 0 0 1-1.4 0l-.1-.1a1 1 0 0 0-1.1-.2 1 1 0 0 0-.6.9V20a1 1 0 0 1-1 1h-1.8a1 1 0 0 1-1-1v-.2a1 1 0 0 0-.6-.9 1 1 0 0 0-1.1.2l-.1.1a1 1 0 0 1-1.4 0L4.3 17.9a1 1 0 0 1 0-1.4l.1-.1a1 1 0 0 0 .2-1.1 1 1 0 0 0-.9-.6H3.5a1 1 0 0 1-1-1v-1.8a1 1 0 0 1 1-1h.2a1 1 0 0 0 .9-.6 1 1 0 0 0-.2-1.1l-.1-.1a1 1 0 0 1 0-1.4l1.3-1.3a1 1 0 0 1 1.4 0l.1.1a1 1 0 0 0 1.1.2 1 1 0 0 0 .6-.9V4a1 1 0 0 1 1-1h1.8a1 1 0 0 1 1 1v.2a1 1 0 0 0 .6.9 1 1 0 0 0 1.1-.2l.1-.1a1 1 0 0 1 1.4 0l1.3 1.3a1 1 0 0 1 0 1.4l-.1.1a1 1 0 0 0-.2 1.1 1 1 0 0 0 .9.6h.2a1 1 0 0 1 1 1v1.8a1 1 0 0 1-1 1h-.2a1 1 0 0 0-.9.6z" />
        </>
      );
    case "database":
      return (
        <>
          <ellipse cx="12" cy="6" rx="7" ry="3" />
          <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
          <path d="M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
        </>
      );
    case "file-text":
      return (
        <>
          <path d="M7 4h7l3 3v13H7z" />
          <path d="M14 4v4h4" />
          <path d="M9.5 12h5" />
          <path d="M9.5 15h5" />
        </>
      );
    case "log-out":
      return (
        <>
          <path d="M10 6H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" />
          <path d="M14 16l5-4-5-4" />
          <path d="M19 12h-9" />
        </>
      );
    case "play":
      return <path d="M9 7.5v9l7-4.5z" />;
    case "pause":
      return (
        <>
          <path d="M9 7h2.5v10H9z" />
          <path d="M12.5 7H15v10h-2.5z" />
        </>
      );
    case "stop":
      return <rect x="8" y="8" width="8" height="8" rx="1.5" />;
    case "skip":
      return (
        <>
          <path d="M6 8.5v7l5-3.5z" />
          <path d="M11 8.5v7l5-3.5z" />
          <path d="M18 8v8" />
        </>
      );
    case "prev":
      return (
        <>
          <path d="M18 8.5v7l-5-3.5z" />
          <path d="M13 8.5v7L8 12z" />
          <path d="M6 8v8" />
        </>
      );
    default:
      return <circle cx="12" cy="12" r="1.5" />;
  }
}
